import DeleteTwoToneIcon from "@mui/icons-material/DeleteTwoTone";
import DescriptionTwoToneIcon from "@mui/icons-material/DescriptionTwoTone";
import EditTwoToneIcon from "@mui/icons-material/EditTwoTone";
import VisibilityIcon from "@mui/icons-material/Visibility";
import {
	Avatar,
	Box,
	Button,
	IconButton,
	TableCell,
	TableRow,
	Tooltip,
	Typography,
	useTheme,
} from "@mui/material";
import moment from "moment";
import React from "react";
import { imgUrl } from "src/Helpers/utils/constants";
import type { IOfflineExhibitorListItem } from "../type/offlineExhibitorType";

interface Props {
	exhibitor: IOfflineExhibitorListItem;
	index: number;
	paginationSkip: number | string;
	onView: (id: number) => void;
	onEdit: (id: number) => void;
	onDelete: (id: number, name: string) => void;
}

const getFullUrl = (path?: string) => {
	if (!path) return "";
	if (path.startsWith("http://") || path.startsWith("https://") || path.startsWith("blob:")) {
		return path;
	}
	return `${imgUrl}/${path}`;
};

const OfflineExhibitorTableRow: React.FC<Props> = ({
	exhibitor,
	index,
	paginationSkip,
	onView,
	onEdit,
	onDelete,
}) => {
	const theme = useTheme();
	const logoUrl = getFullUrl(exhibitor.logo);
	const brochureUrl = getFullUrl(exhibitor.brochure);

	return (
		<TableRow hover key={exhibitor.id}>
			{/* SL */}
			<TableCell>
				<Typography variant="body1" color="text.primary">
					{index + 1 + Number(paginationSkip)}
				</Typography>
			</TableCell>

			{/* Exhibitor No */}
			<TableCell>
				<Typography variant="body2" fontWeight={600} color="primary.main">
					{exhibitor.exhibitor_no || "-"}
				</Typography>
			</TableCell>

			{/* Exhibitor Name + Logo */}
			<TableCell>
				<Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
					{logoUrl ? (
						<Avatar
							src={logoUrl}
							alt={exhibitor.name}
							variant="rounded"
							sx={{
								width: 38,
								height: 38,
								borderRadius: 1.5,
								bgcolor: "background.paper",
								border: "1px solid",
								borderColor: "divider",
								boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
							}}
						/>
					) : (
						<Avatar
							variant="rounded"
							sx={{
								width: 38,
								height: 38,
								borderRadius: 1.5,
								bgcolor: "primary.lighter",
								color: "primary.main",
								fontWeight: 700,
								fontSize: "0.875rem",
							}}
						>
							{exhibitor.name?.charAt(0)?.toUpperCase() || "E"}
						</Avatar>
					)}
					<Box sx={{ overflow: "hidden" }}>
						<Typography color="text.primary" fontWeight={600} noWrap>
							{exhibitor.name}
						</Typography>
						{exhibitor.email && (
							<Typography variant="body1" color="text.secondary" display="block" noWrap>
								{exhibitor.email}
							</Typography>
						)}
					</Box>
				</Box>
			</TableCell>

			{/* Brochure */}
			<TableCell>
				{brochureUrl ? (
					<Tooltip title="View / Download Brochure" arrow>
						<Button
							component="a"
							href={brochureUrl}
							target="_blank"
							rel="noopener noreferrer"
							size="small"
							variant="outlined"
							color="primary"
							startIcon={<DescriptionTwoToneIcon fontSize="small" />}
							sx={{
								borderRadius: 1.5,
								textTransform: "none",
								fontSize: "0.75rem",
								fontWeight: 600,
								py: 0.3,
								px: 1,
								whiteSpace: "nowrap",
							}}
						>
							Brochure
						</Button>
					</Tooltip>
				) : (
					<Typography variant="body2" color="text.secondary">
						-
					</Typography>
				)}
			</TableCell>

			{/* Created At */}
			<TableCell>
				<Typography variant="body2" color="text.secondary">
					{exhibitor.created_at
						? moment(exhibitor.created_at).format("LL")
						: "-"}
				</Typography>
			</TableCell>

			{/* Actions */}
			<TableCell align="right">
				<Box sx={{ display: "flex", justifyContent: "flex-end", gap: 0.5 }}>
					{/* View */}
					<Tooltip title="View Details" arrow>
						<IconButton
							onClick={() => onView(exhibitor.id)}
							sx={{
								"&:hover": {
									background: theme.colors?.primary?.lighter || "rgba(85, 105, 255, 0.1)",
								},
								color: theme.palette.primary.main,
							}}
							size="small"
						>
							<VisibilityIcon fontSize="small" />
						</IconButton>
					</Tooltip>

					{/* Edit */}
					<Tooltip title="Edit Exhibitor" arrow>
						<IconButton
							onClick={() => onEdit(exhibitor.id)}
							sx={{
								"&:hover": {
									background: theme.colors?.success?.lighter || "rgba(68, 214, 0, 0.1)",
								},
								color: theme.palette.success.dark,
							}}
							size="small"
						>
							<EditTwoToneIcon fontSize="small" />
						</IconButton>
					</Tooltip>

					{/* Delete */}
					<Tooltip title="Delete Exhibitor" arrow>
						<IconButton
							onClick={() => onDelete(exhibitor.id, exhibitor.name)}
							sx={{
								"&:hover": {
									background: theme.colors?.error?.lighter || "rgba(255, 25, 67, 0.1)",
								},
								color: theme.palette.error.main,
							}}
							size="small"
						>
							<DeleteTwoToneIcon fontSize="small" />
						</IconButton>
					</Tooltip>
				</Box>
			</TableCell>
		</TableRow>
	);
};

export default OfflineExhibitorTableRow;
